import React, { useState, useCallback, useEffect } from 'react';
import { Fingerprint, Loader2 } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from './AuthProvider';
import MfaChallengePage from './MfaChallengePage';
import { stashReturnUrlIfAuthHandoff, currentAuthHandoffReturnUrl, setupReturnPath } from './authHandoffReturn';
import axios from 'axios';
import { passkeysApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  AuthShell,
  AUTH_ACCENT_BUTTON,
  AUTH_FIELD_CLASS,
  AUTH_FOOTER_LINK_CLASS,
  AUTH_FOOTNOTE_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_OUTLINE_BUTTON,
  AUTH_PRIMARY_BUTTON,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '@/components/auth/AuthShell';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

interface SsoCheckResult {
  ssoEnabled: boolean;
  provider?: string;
  displayName?: string;
  loginUrl?: string;
  enforceSso?: boolean;
  error?: string;
}

function Divider({ label }: { label: string }) {
  return (
    <div className="my-3 flex items-center gap-2">
      <div className="h-px flex-1 bg-foreground/10" />
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="h-px flex-1 bg-foreground/10" />
    </div>
  );
}

// Friendly copy for the ?error= code the backend appends when a social /
// SSO sign-in can't complete — its callback redirects back to the app root
// with that code. The login page previously ignored the param entirely, so
// a bounced user just saw the form again with no explanation.
// Not errors: the sign-in is waiting on something the person does next.
const OAUTH_NOTICE_MESSAGES: Record<string, string> = {
  microsoft_confirm_sent:
    'To finish your first Microsoft sign-in, open the link we’ve emailed to you, in this browser. It confirms the Microsoft account is yours.',
};

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  microsoft_cancelled: 'Microsoft sign-in was cancelled. Try again, or choose another way to sign in.',
  no_account:
    'No Cadence account matches that email address. Social sign-in works only for an existing account — ask your administrator for an invitation, or sign in with the email your account uses. (If you chose “Hide My Email”, Apple sends a private relay address that won’t match an account.)',
  no_email:
    'That provider didn’t share an email address, so we couldn’t match your account. Please try another sign-in method.',
  auth_failed: 'Sign-in with that provider didn’t complete. Please try again.',
  invalid_state:
    'Your sign-in session couldn’t be verified (it may have expired). Please try again.',
  apple_unavailable:
    'Sign in with Apple isn’t available right now. Please try another method.',
  google_unavailable:
    'Sign in with Google isn’t available right now. Please try another method.',
  microsoft_unavailable:
    'Sign in with Microsoft isn’t available right now. Please try another method.',
  microsoft_confirm_invalid:
    'That confirmation link has expired or was already used. Sign in with Microsoft again for a new one.',
  microsoft_confirm_other_browser:
    'Open the confirmation link in the same browser you signed in with. If you signed in from the Cadence app, sign in with Microsoft once at app.stratiumhq.com instead.',
  microsoft_account_mismatch:
    'That Microsoft account isn’t the one linked to your Cadence account. Sign in with the Microsoft account you used before, or with your password or an email link.',
  sso_seat_limit:
    'Your organisation is using all the seats on its plan, so a new account can’t be created. Ask your administrator to free a seat or add one.',
  sso_required:
    'Your organisation requires you to sign in with its single sign-on. Enter your work email and use the SSO button.',
  sso_unavailable:
    'Your organisation’s sign-in isn’t available right now. Try again in a moment; if it keeps happening, ask your admin.',
  sso_invalid_state:
    'Your organisation’s sign-in took too long or was started in another browser. Please start again from here.',
  sso_account_disabled:
    'Your Cadence account is switched off. Ask your organisation’s admin.',
  sso_domain_not_verified:
    'Your organisation’s sign-in isn’t ready for your email domain yet: an admin has to verify the domain first. Sign in another way for now, or ask your admin to invite you.',
};

// Any other code from the organisation's sign-in (the provider refused, a
// token didn't check out): one plain message, with the code for support.
const ssoFallback = (code: string) =>
  `Your organisation’s sign-in didn’t finish (${code}). Try again; if it keeps happening, ask your admin.`;

export default function LoginPage() {
  const { login, requiresMfa, applyTokens } = useAuth();

  // Document title — set per spec so the browser tab / window title
  // reflects the surface. Cheap one-time effect.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Sign in · Cadence';
    return () => { document.title = prev; };
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  /// Shown when the backend says the user must reset password but didn't
  /// supply a token (new-tenant signups where the welcome email carries
  /// the real reset link). Rendered as an info pill below the form, not
  /// as an error. v2.2.5.
  const [checkEmailMessage, setCheckEmailMessage] = useState('');

  const [ssoChecked, setSsoChecked] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);
  const [ssoResult, setSsoResult] = useState<SsoCheckResult | null>(null);

  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  // Entrance — `ready` flips once the page is visible so each `.rise`
  // element settles up from its initial state on its own per-slot
  // delay. Mirrors the Brief surface's triple-redundant trigger
  // (120ms timer + window load + 200ms fallback + visibilitychange)
  // so the reveal fires even if React paints late or the user
  // switches away/back before it finishes.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setReady(true); };
    const t1 = window.setTimeout(reveal, 120);
    const t2 = window.setTimeout(reveal, 200);
    window.addEventListener('load', reveal);
    const onVis = () => { if (document.visibilityState === 'visible') reveal(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('load', reveal);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Surface the error code from an OAuth / SSO callback redirect. It can
  // arrive two ways: (1) directly on the app root as ?error=… (the social
  // sign-in path, e.g. ?error=no_account), or (2) stashed in sessionStorage
  // as `authError` by the /auth/callback component, which consumes the query
  // itself and then bounces to a clean "/". Read BOTH — previously the URL
  // param was swallowed and the sessionStorage value was never read at all,
  // so a bounced user got no explanation either way. Then clear the source so
  // a refresh doesn't re-show it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('error');
    const fromStash = sessionStorage.getItem('authError');
    const code = fromUrl || fromStash;
    if (!code) return;
    if (OAUTH_NOTICE_MESSAGES[code]) setCheckEmailMessage(OAUTH_NOTICE_MESSAGES[code]);
    // Only a well-formed code is echoed: the address is anyone's to write, and
    // "?error=sso_<any text>" put that text on the real sign-in page.
    else setError(OAUTH_ERROR_MESSAGES[code] || (/^sso_[a-z_]{1,40}$/.test(code) ? ssoFallback(code) : 'Sign-in failed. Please try again.'));
    if (fromStash) sessionStorage.removeItem('authError');
    if (fromUrl) {
      params.delete('error');
      const qs = params.toString();
      const cleaned = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleaned);
    }
  }, []);

  // v2.2.3 — WebAuthn conditional UI. Kicks off a passkey assertion in
  // "autofill" mode on mount so password managers (NordPass, 1Password,
  // iCloud Keychain, Bitwarden, etc.) can surface the user's Cadence
  // passkey as an autocomplete suggestion when they focus the email
  // field — no button click required. The email input also gains
  // `autocomplete="username webauthn"` to associate the discovery with
  // that field. User-reported 2026-06-03: NordPass wasn't showing the
  // passkey for selection. Cause: we only ever called startAuthentication
  // as a button handler (explicit modal flow), never with the conditional
  // mediation that turns it into a discoverable autocomplete option.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (typeof PublicKeyCredential === 'undefined' ||
            typeof (PublicKeyCredential as any).isConditionalMediationAvailable !== 'function') {
          return;
        }
        const available = await (PublicKeyCredential as any).isConditionalMediationAvailable();
        if (!available || cancelled) return;

        const options = await passkeysApi.loginOptions();
        const challengeKey = options.challenge;
        const assertion = await startAuthentication({
          optionsJSON: options,
          useBrowserAutofill: true,
        });
        if (cancelled) return;
        const result = await passkeysApi.login({ response: assertion, challengeKey });
        setLeaving(true);
        await new Promise((r) => setTimeout(r, 250));
        applyTokens(result);
        const onAuthHandoffFlow =
          window.location.pathname.startsWith('/oauth/authorize')
          || window.location.pathname.startsWith('/oauth/desktop');
        if (!onAuthHandoffFlow && window.location.pathname !== '/' && !setupReturnPath()) {
          window.history.replaceState(null, '', '/');
        }
      } catch (err: any) {
        // Conditional UI cancellations are silent by design — the user
        // either chose not to use the suggested passkey or no passkey
        // matched. Don't surface errors here; the explicit "Sign in with
        // passkey" button (handlePasskeyLogin) does show errors when
        // intentionally clicked. The one exception: a passkey that worked
        // but the organisation requires its own sign-in, which the person
        // can act on.
        if (!cancelled && err?.response?.data?.ssoEnforced) setError(err.response.data.error);
      }
    })();
    return () => { cancelled = true; };
    // applyTokens comes from useAuth() and is stable for the component
    // lifetime; safe to omit. We don't want this effect to re-run on
    // every render — the conditional UI session starts once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSso = useCallback(async (emailValue: string) => {
    if (!emailValue || !emailValue.includes('@')) return;
    setSsoChecking(true);
    try {
      const { data } = await axios.get<SsoCheckResult>(
        `${API_URL}/sso/login?email=${encodeURIComponent(emailValue)}`,
      );
      setSsoResult(data);
      setSsoChecked(true);
    } catch {
      setSsoResult(null);
      setSsoChecked(true);
    } finally {
      setSsoChecking(false);
    }
  }, []);

  // Leave-veil — a soft light overlay that fades up on a successful
  // login so the dark auth surface brightens into the workspace rather
  // than flashing. Stays mounted because AuthGate unmounts LoginPage
  // the instant isAuthenticated flips — the workspace's own entrance
  // picks the visual up on the other side.
  const [leaving, setLeaving] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      // If the backend asked for MFA, leave the page in place so the
      // MFA challenge form can render. Setting `leaving=true` would
      // fade the veil up over the MFA form and stall the user.
      if (result.requiresMfa) {
        return;
      }
      // Backend says the user must reset password BUT didn't supply a
      // token (EmailConfirmed=false branch — typical new-tenant signup
      // where the welcome email carries the real reset link). Show the
      // message inline rather than redirecting to a broken
      // /reset-password page with an empty token.
      if (result.checkEmailMessage) {
        setCheckEmailMessage(result.checkEmailMessage);
        return;
      }
      // Soft veil before the AuthGate swap — gives the transition
      // ~250ms of visible fade-up so the page brightens rather than
      // cuts.
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.ssoEnforced) {
        setError('Your organisation requires SSO login. Use the SSO button below.');
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
      setLeaving(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (ssoChecked) {
      setSsoChecked(false);
      setSsoResult(null);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetEmail = magicLinkEmail || email;
    if (!targetEmail) return;
    setError('');
    setMagicLinkLoading(true);
    try {
      // Stash now so that if the user clicks the email link in this same
      // browser session (sessionStorage is per-tab but per-origin),
      // /auth/callback returns them to /oauth/desktop?session=… afterward.
      stashReturnUrlIfAuthHandoff();
      await axios.post(`${API_URL}/auth/magic-link`, { email: targetEmail });
      setMagicLinkSent(true);
    } catch {
      // API always returns 200 to prevent enumeration; only network errors land here.
      setError('Unable to send magic link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      const options = await passkeysApi.loginOptions(email || undefined);
      const challengeKey = options.challenge;
      const assertion = await startAuthentication({ optionsJSON: options });
      const result = await passkeysApi.login({ response: assertion, challengeKey });

      // Soft veil before the AuthGate swap — same treatment as the
      // password path so successful passkey login doesn't flash
      // dark → bright → dark. Fades up over 200ms; 250ms later
      // applyTokens hands off to the workspace's own entrance.
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));

      // Apply tokens through React state so OAuthAuthorizePage keeps its query
      // string. Writing sessionStorage + window.location.reload() was unreliable
      // on the authorize flow — some browsers dropped the search params.
      applyTokens(result);
      // Preserve the URL for any auth-handoff flow (OAuth-for-MCP consent
      // and the Mac desktop sign-in handoff). Passkey is itself a strong
      // factor so both flows can complete on passkey alone — no MFA hop.
      const onAuthHandoffFlow =
        window.location.pathname.startsWith('/oauth/authorize')
        || window.location.pathname.startsWith('/oauth/desktop');
      if (!onAuthHandoffFlow && window.location.pathname !== '/' && !setupReturnPath()) {
        window.history.replaceState(null, '', '/');
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else if (err.response?.status === 401) {
        // The browser sent a credential Cadence doesn't recognise (e.g. a
        // passkey saved in a third-party manager that was never registered
        // here). Point the user at a path forward instead of a bare failure.
        setError('This passkey isn’t registered with Cadence. Use a different sign-in method, or register this passkey in Settings → Security.');
      } else {
        setError(err.response?.data?.error || 'Passkey authentication failed. Please try again.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const showSsoButton = ssoChecked && ssoResult?.ssoEnabled;

  // When this login page is rendered inside a native desktop-handoff
  // (/oauth/desktop) — e.g. the in-app browser of the Mac/iOS apps — thread the
  // handoff URL to the backend as a returnUrl query param. The server echoes it
  // back on the OAuth success redirect, so the cadence:// handoff survives even
  // if the in-app browser drops the sessionStorage stash. No-op on the normal
  // web login (returns null → empty suffix).
  const handoffReturn = currentAuthHandoffReturnUrl();
  const oauthReturnSuffix = handoffReturn
    ? `?returnUrl=${encodeURIComponent(handoffReturn)}`
    : '';

  // Hide all third-party social sign-in (Microsoft / Apple) when this page is
  // rendered inside the NATIVE app's in-app browser (the /oauth/desktop
  // handoff used by the iOS + Mac apps). Two reasons, both App Store rejections:
  // (1) social sign-in only works for an already-linked account, so a reviewer
  // using their own Apple/Microsoft ID gets "no account" and is bounced back to
  // the login page — read as a bug (Guideline 2.1a). (2) Offering Microsoft but
  // not Apple (or vice-versa) trips Guideline 4.8; offering NEITHER in the app
  // sidesteps 4.8 entirely. The reviewer (and real users) sign in here with
  // email/password, passkey, or magic-link. The normal WEB login (/) is
  // unaffected and keeps Microsoft for real users with linked accounts.
  const isNativeAppSignIn = window.location.pathname.startsWith('/oauth/desktop');

  return (
    // AuthShell draws the backdrop (copper halo, blue glow, vignette, grain)
    // and the leave-veil, which fades up to ink on a successful sign-in so
    // the hand-off to the app is dark → dark rather than a flash.
    // The form column sits directly on the backdrop: no card chrome. Each
    // `.rise` element animates up from translateY(16px) + opacity 0 on its
    // own `--d` delay once `ready` flips, giving the surface the same
    // staggered entrance the Brief uses.
    <AuthShell leaving={leaving} width="form">
      <div
        className={cn('rise mb-5', ready && 'is-revealed')}
        style={{ '--d': '120ms' } as React.CSSProperties}
      >
        <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
      </div>

      <h1
        className={cn(
          // AUTH_TITLE_CLASS is smaller on a phone so the form clears the
          // iOS keyboard inside the ASWebAuthenticationSession sheet; sm:
          // restores the full editorial scale at iPad / desktop widths.
          'rise mb-2 sm:mb-[10px]',
          AUTH_TITLE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '260ms' } as React.CSSProperties}
      >
        The operating system<br />for executive teams.
      </h1>
      <p
        className={cn(
          'rise mb-5 sm:mb-[30px]',
          AUTH_SUBTITLE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '380ms' } as React.CSSProperties}
      >
        Sign in to your day.
      </p>

      {error && (
        <Alert variant="error" className="mb-3 rounded-xl">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {checkEmailMessage && !error && (
        <Alert variant="info" className="mb-3 rounded-xl border-primary/30 bg-primary/[0.08]">
          <AlertDescription className="text-foreground/85">{checkEmailMessage}</AlertDescription>
        </Alert>
      )}

      {!requiresMfa ? (
        <>
          <form
            onSubmit={handleLogin}
            className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
            style={{ '--d': '520ms' } as React.CSSProperties}
          >
            <div className="flex flex-col gap-[7px]">
              <Label htmlFor="login-email" className={AUTH_LABEL_CLASS}>
                Work email
              </Label>
              <Input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={() => { if (email && !ssoChecked) checkSso(email); }}
                autoFocus
                autoComplete="username webauthn"
                className={AUTH_FIELD_CLASS}
              />
            </div>

            {/* Password field rendered from first paint — the spec is a
                single Sign in submit, not a two-stage Continue→Sign in.
                SSO detection still runs on email blur, but it's an
                informational hint, not a gate on the password field. */}
            <div className="flex flex-col gap-[7px]">
              <Label htmlFor="login-password" className={AUTH_LABEL_CLASS}>
                Password
              </Label>
              <Input
                id="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className={AUTH_FIELD_CLASS}
              />
            </div>
            <div className="text-right">
              <a
                href="/forgot-password"
                className="text-xs text-muted-foreground hover:text-primary hover:underline"
              >
                Forgot password?
              </a>
            </div>
            <Button
              type="submit"
              disabled={loading || !email || !password}
              className={cn('mt-[6px]', AUTH_PRIMARY_BUTTON)}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign in'}
            </Button>

            {ssoChecking && (
              <div className="my-3 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Inline SSO button — surfaces when the domain has an SSO
                provider configured. Optional alternative to the password
                submit above; not a gate. */}
            {showSsoButton && (
              <Button
                type="button"
                className={cn('mt-3 normal-case', AUTH_PRIMARY_BUTTON)}
                asChild
              >
                <a
                  href={ssoResult?.loginUrl || '#'}
                  onClick={() => stashReturnUrlIfAuthHandoff()}
                >
                  {ssoResult?.displayName || `Sign in with ${ssoResult?.provider || 'SSO'}`}
                </a>
              </Button>
            )}

            {/* Enforce-SSO advisory — shown after the SSO check confirms
                the org is SSO-only. The password submit will fail with a
                matching error; the advisory just sets expectations. */}
            {ssoResult?.ssoEnabled && ssoResult.enforceSso && (
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Your organisation requires SSO authentication.
              </p>
            )}
          </form>

          {/* OAuth + passkey + magic-link options — rendered alongside
              the password form rather than gated behind the previous
              two-stage Continue→Sign in flow. Microsoft / Google /
              passkey / email-link are intentionally kept (per current
              product scope) even though the redesign spec calls for
              Microsoft only. Wrapped in a single .rise slot so the
              cluster reveals together at 760ms. */}
          <div
            className={cn('rise', ready && 'is-revealed')}
            style={{ '--d': '760ms' } as React.CSSProperties}
          >
            <Divider label="or" />

            {/* Third-party social sign-in (Microsoft / Apple) is hidden in the
                native app's in-app browser (isNativeAppSignIn) — see the note
                by its definition above. Shown only on the normal web login. */}
            {!isNativeAppSignIn && (
              <>
                <Button
                  asChild
                  className={cn('mb-2', AUTH_OUTLINE_BUTTON)}
                  variant="outline"
                  onClick={() => { stashReturnUrlIfAuthHandoff(); setLeaving(true); }}
                >
                  <a href={`${API_URL}/auth/login/microsoft${oauthReturnSuffix}`}>
                    Continue with Microsoft
                  </a>
                </Button>

                {/* "Sign in with Google" is intentionally NOT rendered: Google
                    OAuth is not configured in production (no Authentication:Google
                    settings on cadence-prod-api), so the button would dead-end —
                    this was the App Store "error page on Sign in with Google"
                    rejection. To restore: configure a Google OAuth client (redirect
                    https://api.stratiumhq.com/api/auth/callback/Google) + the Azure
                    app settings, then re-add the button:
                    <a href={`${API_URL}/auth/login/google${oauthReturnSuffix}`}>Sign in with Google</a>
                    Backlogged in BACKLOG.md. */}

                {/* Sign in with Apple — required by Guideline 4.8 wherever
                    third-party social sign-in (Microsoft above) is offered.
                    Hidden in the native app (no social there → 4.8 N/A). */}
                <Button
                  asChild
                  className={AUTH_OUTLINE_BUTTON}
                  variant="outline"
                  onClick={() => { stashReturnUrlIfAuthHandoff(); setLeaving(true); }}
                >
                  <a href={`${API_URL}/auth/login/apple${oauthReturnSuffix}`}>
                    Sign in with Apple
                  </a>
                </Button>
              </>
            )}

            <Button
              variant="outline"
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading}
              className={cn('mt-2 gap-2', AUTH_ACCENT_BUTTON)}
            >
              {passkeyLoading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Fingerprint className="h-4 w-4" />}
              Sign in with passkey
            </Button>

            {!showMagicLink && (
              <Button
                variant="ghost"
                onClick={() => { setShowMagicLink(true); setMagicLinkEmail(email); }}
                className="mt-2 h-10 w-full text-sm font-medium normal-case text-muted-foreground hover:bg-primary/5 hover:text-primary"
              >
                Sign in with email link
              </Button>
            )}
          </div>

          {showMagicLink && !magicLinkSent && (
            <div className="mt-3">
              <Divider label="email link" />
              <form onSubmit={handleSendMagicLink}>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ml-email" className="text-muted-foreground">Email for magic link</Label>
                  <Input
                    id="ml-email"
                    type="email"
                    required
                    value={magicLinkEmail}
                    onChange={(e) => setMagicLinkEmail(e.target.value)}
                    className={AUTH_FIELD_CLASS}
                  />
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  disabled={magicLinkLoading || !magicLinkEmail}
                  className={cn('mt-3 gap-2', AUTH_ACCENT_BUTTON)}
                >
                  {magicLinkLoading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : null}
                  Send magic link
                </Button>
              </form>
            </div>
          )}

          {magicLinkSent && (
            <Alert variant="success" className="mt-3 rounded-xl">
              <AlertDescription>
                Check your email for a login link. It expires in 15 minutes.
              </AlertDescription>
            </Alert>
          )}
        </>
      ) : (
        // Second factor — extracted into its own component (shares the
        // AuthProvider MFA context, so verifyMfa completes the in-flight
        // challenge). Kept as a component, not a route, so the in-memory
        // mfaToken survives.
        <MfaChallengePage />
      )}

      {/* Footer row — per the spec: "Forgot password · 14 day free
          trial" instead of the previous "Don't have an account? Start
          free trial" violet link. The forgot-password CTA up by the
          password field is the actual destination; this is just the
          footer affordance line. Slots at the tail of the .rise
          ladder (1000ms) so the surface lands top-to-bottom. */}
      <div
        className={cn(
          'rise mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground',
          ready && 'is-revealed',
        )}
        style={{ '--d': '1000ms' } as React.CSSProperties}
      >
        <a href="/forgot-password" className={AUTH_FOOTER_LINK_CLASS}>
          Forgot password
        </a>
        {/* The "14 day free trial" link routes to /signup, which is the
            web (Stripe) checkout. App Review Guideline 3.1.1 forbids
            surfacing any non-IAP purchase path from inside the app, and the
            native iOS/Mac apps render THIS login page inside an in-app
            browser (isNativeAppSignIn) during "Sign in". A reviewer tapped
            it there and reached checkout.stripe.com — the round-6 rejection.
            Hide it in the native context; the App Store build's only
            purchase route is Apple IAP. Real-browser web login keeps it. */}
        {!isNativeAppSignIn && (
          <>
            <span aria-hidden className="text-foreground/30">·</span>
            <a href="/signup" className={AUTH_FOOTER_LINK_CLASS}>
              14 day free trial
            </a>
          </>
        )}
      </div>

      {/* Stratium provenance — quiet anchor line at the bottom of the
          auth surface, mirroring Signup. */}
      <p
        className={cn(
          'rise mt-8 text-center',
          AUTH_FOOTNOTE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '1200ms' } as React.CSSProperties}
      >
        Cadence is the product. Stratium is the company behind it.
      </p>
    </AuthShell>
  );
}
